import {
  Modal,
  Button,
  Grid,
  Stack,
  Flex,
  Box,
  ScrollArea,
  Paper,
} from '@mantine/core';
import { schemaResolver } from '@mantine/form';
import { useImperativeHandle, useState } from 'react';
import {
  useConfirmModal,
  PromiseConfirmModal,
} from '#/components/ui/messages/components/confirmModal';

import {
  ActionSettingFormValue,
  toActionSetting,
  ValidActionSettingFormValueSchema,
} from '#/components/ui/messages/model';

import { ActionEditorFormProvider, useActionEditorForm } from './context';

import { ActionVariants } from './variants';

import { ActionSettingPreview } from './ActionSettingPreview';
import { ActionSettingDialogRef } from './useActionSettingDialog';

type ActionSettingDialogSession = {
  input: ActionSettingFormValue;
  onConfirm: (value: ActionSettingFormValue) => Promise<void>;
  onLeave: () => Promise<void>;
};

const ActionSettingFields = ({
  type,
}: {
  type: ActionSettingFormValue['type'];
}) => {
  const Fields = ActionVariants[type].Fields;

  return <Fields />;
};

export const ActionSettingDialog = ({
  actionSettingRef,
}: ActionSettingDialogRef) => {
  const [state, setState] = useState<ActionSettingDialogSession>();

  const { props, open } = useConfirmModal();

  const form = useActionEditorForm({
    mode: 'controlled',
    validate: schemaResolver(ValidActionSettingFormValueSchema, { sync: true }),
    initialValues: state?.input,
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const opened = !!state;

  const type = state?.input.type;

  useImperativeHandle(actionSettingRef, () => {
    const edit = async (input: ActionSettingFormValue) => {
      const editPromise = new Promise<ActionSettingFormValue | undefined>(
        (resolve) => {
          // init
          form.setInitialValues(input);
          // 我不知道怎麼處理這裡的型別問題 damn
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          form.setValues(input as any);

          setState({
            input,
            onConfirm: async (value) => {
              setState(undefined);
              form.reset();
              resolve(value);
            },
            onLeave: async () => {
              const shouldLeave = await open?.(true);

              if (!shouldLeave) return;

              form.reset();
              setState(undefined);
              resolve(undefined);
            },
          });
        },
      );

      const value = await editPromise;

      return value;
    };

    return {
      edit,
      value: form,
    };
  }, [open, form]);

  const previewValue = toActionSetting(form.getValues());

  if (!opened) {
    return null;
  }

  if (type === undefined) {
    return null;
  }

  const width = type === 'open_link' ? 500 : 1000;
  const height = type === 'open_link' ? 400 : 600;
  const title = `設定 ${ActionVariants[type].name}`;
  const Preview = ActionVariants[type].Preview;
  const Fields = ActionVariants[type].Fields;
  const Actions = ActionVariants[type]?.Actions;

  const content = (() => {
    if (!Preview) {
      return (
        <Grid.Col span={{ base: 12, md: 12 }}>
          <Box>
            <ScrollArea h="100%" pr="13px">
              <Stack>
                <Fields />
              </Stack>
            </ScrollArea>
          </Box>
        </Grid.Col>
      );
    }

    return (
      <>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Box className="h-150">
            <ScrollArea h="100%" pr="13px">
              <Stack>
                <ActionSettingFields type={type} />
              </Stack>
            </ScrollArea>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder bg="gray.0" p="md" radius="md" mih={220} h="100%">
            <Flex justify="center" align="center" h="100%">
              <ActionSettingPreview value={previewValue} />
            </Flex>
          </Paper>
        </Grid.Col>
      </>
    );
  })();

  return (
    <Modal opened={opened} onClose={state.onLeave} size={width} title={title}>
      <PromiseConfirmModal {...props} />
      <ActionEditorFormProvider form={form}>
        <Stack>
          <Grid h={height}>{content}</Grid>
          <Flex justify="flex-end" gap={10}>
            {Actions && <Actions />}
            <Button variant="outline" onClick={state.onLeave}>
              取消
            </Button>
            <Button
              disabled={!form.isValid()}
              onClick={() => {
                const { hasErrors } = form.validate();

                if (hasErrors) return;

                state.onConfirm(form.getValues());
              }}
            >
              確定
            </Button>
          </Flex>
        </Stack>
      </ActionEditorFormProvider>
    </Modal>
  );
};
